import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';

import Pagination from 'components/pagination/Pagination';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import InsightsReportsPosts from 'modules/insights/reports/components/posts/collabary/InsightsReportsPosts';
import InsightsReportsPostsFilters from 'modules/insights/reports/components/posts/collabary/filters/InsightsReportsPostsFilters';

import { postsUpdateScreenState } from 'modules/insights/reports/reducers/posts/collabary/insights-reports-posts';

import { __t, applyTranslations } from 'utils/language';
import { INSIGHTS_SORT_LIST_INSTAGRAM } from 'utils/insights';

import './insights-reports-analysis-posts.scss';

import type {
  Data,
  LocationWindow,
  MentionCardModalItem,
  MixedSelectDropdownItem
} from 'types';
import type {
  PostScreenState,
  Query,
  ReportType
} from 'modules/insights/reports/types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('modules/insights/reports/shared/insights-reports-shared.i18n.json'));
applyTranslations(require('utils/i18n/insights.i18n.json'));

type OwnProps = {
  reportType: ReportType,
  queries: Array<Query>,
  location: LocationWindow,
  onFetchPosts: () => void
};

type StateProps = {
  posts: Array<MentionCardModalItem>,
  postsScreenState: PostScreenState,
  isLoading: boolean
};

type DispatchProps = {
  dispatchUpdateScreenState: (value: any, propName: string) => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  sort: string
};

const COMMON_MSG_KEY = 'collabary_common';
const SHARED_MSG_KEY = 'sov_shared';
const MSG_KEY = 'insights';

class InsightsReportsAnalysisPosts extends React.PureComponent<Props, State> {
  state = {
    sort: this.props.postsScreenState.sort || 'most_recent'
  };

  mentionsRef = React.createRef<HTMLDivElement>();

  getDropdownItems = (): Array<MixedSelectDropdownItem> => {
    const { sort } = this.state;
    const sortList = INSIGHTS_SORT_LIST_INSTAGRAM;

    return Object.keys(sortList)
      .map(key => ({
        identifier: key,
        isDisabled: false,
        isSelected: sort === key,
        label: __t(`${MSG_KEY}.sort.${sortList[key]}`),
        value: key
      }));
  };

  handleSortChange = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => {
    const { dispatchUpdateScreenState, onFetchPosts } = this.props;
    const value = selectedItem?.identifier || updatedItems[0].identifier;

    this.setState({
      sort: value
    });

    dispatchUpdateScreenState(value, 'sort');
    onFetchPosts();
  };

  handlePostsPageChange = (page: number): void => {
    const { dispatchUpdateScreenState, onFetchPosts } = this.props;

    if (this.mentionsRef.current) {
      this.mentionsRef.current.scrollIntoView();
    }

    dispatchUpdateScreenState(page, 'page');
    onFetchPosts();
  };

  renderPagination(): React.ReactNode | undefined | null {
    const { postsScreenState, location, isLoading } = this.props;

    return !isLoading && (
      <Pagination
        location={location}
        meta={postsScreenState.pagination}
        links={postsScreenState.pagination.links}
        onClick={this.handlePostsPageChange}
        scrollToTopOnPageChange={false}
      />
    );
  }

  renderSort(): React.ReactNode {
    const { sort } = this.state;
    const dropdownItems = this.getDropdownItems();

    return (
      <div className="insights-reports-analysis-posts__sort">
        <div className="insights-reports-analysis-posts__sort-label">
          {__t(`${MSG_KEY}.sort.description`)}:
        </div>
        <MixedSelectDropdown
          key={`insights-reports-analysis-posts__dropdown--${sort}`}
          name="insights-reports-analysis-posts__dropdown"
          items={dropdownItems}
          withSelectedInList
          className="insights-reports-analysis-posts__dropdown"
          onItemsToggle={this.handleSortChange}
        />
      </div>
    );
  }

  renderFilters(): React.ReactNode {
    const { queries, reportType, postsScreenState, onFetchPosts } = this.props;

    return (
      <InsightsReportsPostsFilters
        queries={queries}
        selectedQueryIds={postsScreenState.filters.queryIds}
        profileTypes={postsScreenState.filters.profileTypes}
        tiers={postsScreenState.filters.tiers}
        reportType={reportType}
        onChange={onFetchPosts}
      />
    );
  }

  renderPosts(): React.ReactNode {
    const { posts, reportType, isLoading } = this.props;

    if (!isLoading && isEmpty(posts)) {
      return (
        <div className="insights-reports-analysis-section__empty-container">
          {__t(`${SHARED_MSG_KEY}.messages.no_results`)}
        </div>
      );
    }

    return (
      <InsightsReportsPosts
        posts={posts}
        channel={reportType}
      />
    );
  }

  render(): React.ReactNode {
    return (
      <div className="insights-reports-analysis-posts">
        <div
          className="insights-reports-analysis-posts__header"
          ref={this.mentionsRef}
        >
          <div>
            {__t(`${COMMON_MSG_KEY}.kpis.mentions`)}
          </div>
        </div>
        {this.renderFilters()}
        {this.renderSort()}
        {this.renderPosts()}
        {this.renderPagination()}
      </div>
    );
  }
}

const mapStateToProps = ({ insightsReportsPosts }): StateProps => ({
  posts: insightsReportsPosts.posts,
  isLoading: insightsReportsPosts.isLoading,
  postsScreenState: insightsReportsPosts.screenState
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchUpdateScreenState: (value: any, propName: string) => (
    dispatch(postsUpdateScreenState(value, propName))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(InsightsReportsAnalysisPosts);
